import { useState, useEffect, useRef } from 'react';
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  type ColumnOrderState,
  type ColumnSizingState,
  type RowSelectionState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Calendar } from 'lucide-react';

import { DataTablePagination } from './DataTablePagination';
import { DataTableToolbar } from './DataTableToolbar';
import { FkFilterPopover } from './FkFilterPopover';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  totalCount: number;
  page: number;
  pageSize: number;
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
  activeFilters: Record<string, string>;
  onSortingChange: (sorting: SortingState) => void;
  onColumnVisibilityChange: (visibility: VisibilityState) => void;
  onColumnOrderChange: (order: ColumnOrderState) => void;
  onColumnSizingChange: (sizing: ColumnSizingState) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onActiveFiltersChange: (filters: Record<string, string>) => void;
  onClearFilters: () => void;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
  onDeleteSelected?: (ids: (string | number)[]) => void;
  getRowId?: (row: TData) => string;
  onEditRow?: (row: TData) => void;
  dateColumns?: Set<string>;
  /** Boolean columns get a tri-state dropdown (All / Yes / No) instead of free-text. */
  booleanColumns?: Set<string>;
  /** Map of column id -> related table name, for FK columns rendered as a multi-select picker. */
  fkColumns?: Record<string, string>;
  /** Map of related table name -> { id: displayLabel } used to populate the FK picker. */
  fkLookups?: Record<string, Record<string, string>>;
}

export function DataTable<TData>({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  sorting,
  columnVisibility,
  columnOrder,
  columnSizing,
  activeFilters,
  onSortingChange,
  onColumnVisibilityChange,
  onColumnOrderChange,
  onColumnSizingChange,
  onPageChange,
  onPageSizeChange,
  onActiveFiltersChange,
  onClearFilters,
  isLoading,
  title,
  subtitle,
  onDeleteSelected,
  getRowId,
  onEditRow,
  dateColumns,
  booleanColumns,
  fkColumns,
  fkLookups
}: DataTableProps<TData>) {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(activeFilters);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setColumnFilters(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    return () => {
      if (filterDebounceRef.current) {
        clearTimeout(filterDebounceRef.current);
      }
    };
  }, []);

  const handleColumnFilterInput = (columnId: string, value: string) => {
    const newFilters = { ...columnFilters };
    if (value) {
      newFilters[columnId] = value;
    } else {
      delete newFilters[columnId];
    }
    setColumnFilters(newFilters);

    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
    }
    filterDebounceRef.current = setTimeout(() => {
      onActiveFiltersChange(newFilters);
    }, 400);
  };

  useEffect(() => {
    setRowSelection({});
  }, [page, pageSize]);

  const selectionColumn: ColumnDef<TData, unknown> = {
    id: '_select',
    header: ({ table: t }) => (
      <Checkbox
        checked={t.getIsAllPageRowsSelected()}
        onCheckedChange={value => t.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={value => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 40,
    minSize: 40,
    maxSize: 40
  };

  const allColumns = [selectionColumn, ...columns];

  const selectedCount = Object.keys(rowSelection).length;
  const handleDeleteSelected = () => {
    if (!onDeleteSelected) {
      return;
    }
    const selectedIds = Object.keys(rowSelection).map(id => {
      const num = Number(id);
      return isNaN(num) ? id : num;
    });
    onDeleteSelected(selectedIds);
    setRowSelection({});
  };

  const table = useReactTable({
    data,
    columns: allColumns,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      rowSelection,
      pagination: { pageIndex: page - 1, pageSize }
    },
    onSortingChange: updater => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(newSorting);
    },
    onColumnVisibilityChange: updater => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      onColumnVisibilityChange(newVisibility);
    },
    onColumnOrderChange: updater => {
      const newOrder = typeof updater === 'function' ? updater(columnOrder) : updater;
      onColumnOrderChange(newOrder);
    },
    onColumnSizingChange: updater => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      onColumnSizingChange(newSizing);
    },
    onPaginationChange: updater => {
      const newPagination = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize }) : updater;
      if (newPagination.pageSize !== pageSize) {
        onPageSizeChange(newPagination.pageSize);
        onPageChange(1);
      } else {
        onPageChange(newPagination.pageIndex + 1);
      }
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...(getRowId ? { getRowId: getRowId as any } : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    manualSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange'
  });

  const selectedRow =
    selectedCount === 1 ? (table.getRowModel().rows.find(r => r.getIsSelected())?.original ?? null) : null;

  const handleEditSelected = () => {
    if (onEditRow && selectedRow) {
      onEditRow(selectedRow);
    }
  };

  // Auto-fit columns to fill container width on mount
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    // Use requestAnimationFrame to ensure the container has been laid out
    requestAnimationFrame(() => {
      const containerWidth = el.clientWidth;
      if (containerWidth <= 0) {
        return;
      }

      const visibleCols = table.getVisibleLeafColumns();
      const totalCurrentSize = visibleCols.reduce((sum, col) => sum + col.getSize(), 0);

      if (totalCurrentSize < containerWidth) {
        const newSizing: Record<string, number> = {};
        const selectCol = visibleCols.find(c => c.id === '_select');
        const selectSize = selectCol ? selectCol.getSize() : 0;
        const availableWidth = containerWidth - selectSize;
        const resizableCols = visibleCols.filter(c => c.id !== '_select');
        const resizableTotal = resizableCols.reduce((sum, col) => sum + col.getSize(), 0);

        for (const col of resizableCols) {
          newSizing[col.id] = Math.floor((col.getSize() / resizableTotal) * availableWidth);
        }

        onColumnSizingChange(newSizing);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  const columnSizeVars = (() => {
    const headers = table.getFlatHeaders();
    const vars: Record<string, string> = {};
    for (const header of headers) {
      vars[`--header-${header.id}-size`] = `${header.getSize()}px`;
      vars[`--col-${header.column.id}-size`] = `${header.column.getSize()}px`;
    }
    return vars;
  })();

  return (
    <div className="flex flex-col h-full min-h-0">
      <DataTableToolbar
        table={table}
        title={title}
        subtitle={subtitle}
        activeFilters={activeFilters}
        onClearFilters={onClearFilters}
        onColumnOrderChange={onColumnOrderChange}
        selectedCount={selectedCount}
        onDeleteSelected={onDeleteSelected ? handleDeleteSelected : undefined}
        onEditSelected={onEditRow ? handleEditSelected : undefined}
      />
      <div ref={containerRef} className="flex-1 min-h-0 rounded-md border overflow-auto">
        <Table style={{ ...columnSizeVars, width: table.getTotalSize() }}>
          <TableHeader className="sticky top-0 z-10 bg-background">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead
                    key={header.id}
                    className="whitespace-nowrap relative"
                    style={{ width: `var(--header-${header.id}-size)` }}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1">
                        {header.column.getCanSort() ? (
                          <button
                            className="flex items-center gap-1 hover:text-foreground"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {header.column.getIsSorted() === 'desc' ? (
                              <ArrowDown className="size-3 shrink-0" />
                            ) : header.column.getIsSorted() === 'asc' ? (
                              <ArrowUp className="size-3 shrink-0" />
                            ) : (
                              <ArrowUpDown className="size-3 text-muted-foreground/50 shrink-0" />
                            )}
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </div>
                    )}
                    {/* Resize handle */}
                    {header.column.getCanResize() && (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                        className={cn(
                          'absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none',
                          'hover:bg-primary/50',
                          header.column.getIsResizing() && 'bg-primary'
                        )}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
            {/* Per-column filter inputs row */}
            <TableRow>
              {table.getHeaderGroups()[0]?.headers.map(header => (
                <TableHead
                  key={`filter-${header.id}`}
                  className="px-1 py-1"
                  style={{ width: `var(--col-${header.id}-size)` }}
                >
                  {!header.isPlaceholder && header.column.getCanSort()
                    ? (() => {
                        if (dateColumns?.has(header.id)) {
                          const filterVal = columnFilters[header.id] ?? '';
                          const [from, to] = filterVal.split('..');
                          const hasFilter = !!(from || to);
                          const displayText = hasFilter ? `${from || '...'} — ${to || '...'}` : '';
                          return (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button
                                  className={cn(
                                    'flex items-center w-full h-7 rounded-sm border border-muted-foreground/20 bg-transparent dark:bg-input/30 px-2 text-xs text-left',
                                    hasFilter ? 'text-foreground' : 'text-muted-foreground'
                                  )}
                                >
                                  <Calendar className="size-3 mr-1.5 shrink-0 text-muted-foreground" />
                                  <span className="truncate">{displayText}</span>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-3" align="start">
                                <div className="space-y-2">
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">From</Label>
                                    <input
                                      type="date"
                                      value={from ?? ''}
                                      onChange={e => {
                                        const currentTo = to ?? '';
                                        const newFrom = e.target.value;
                                        const val = newFrom || currentTo ? `${newFrom}..${currentTo}` : '';
                                        handleColumnFilterInput(header.id, val);
                                      }}
                                      className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring [color-scheme:dark]"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">To</Label>
                                    <input
                                      type="date"
                                      value={to ?? ''}
                                      onChange={e => {
                                        const currentFrom = from ?? '';
                                        const newTo = e.target.value;
                                        const val = currentFrom || newTo ? `${currentFrom}..${newTo}` : '';
                                        handleColumnFilterInput(header.id, val);
                                      }}
                                      className="flex h-8 w-full rounded-md border border-input bg-transparent px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring [color-scheme:dark]"
                                    />
                                  </div>
                                  {hasFilter && (
                                    <button
                                      onClick={() => handleColumnFilterInput(header.id, '')}
                                      className="text-xs text-muted-foreground hover:text-foreground"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          );
                        }
                        if (booleanColumns?.has(header.id)) {
                          const boolVal = columnFilters[header.id] ?? '';
                          return (
                            <select
                              value={boolVal}
                              onChange={e => handleColumnFilterInput(header.id, e.target.value)}
                              className="flex h-7 w-full items-center rounded-sm border border-muted-foreground/20 bg-transparent px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring dark:bg-input/30"
                            >
                              <option value="">All</option>
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          );
                        }
                        const fkTable = fkColumns?.[header.id];
                        const fkLookup = fkTable ? fkLookups?.[fkTable] : undefined;
                        if (fkLookup) {
                          return (
                            <FkFilterPopover
                              lookup={fkLookup}
                              value={columnFilters[header.id] ?? ''}
                              onChange={val => handleColumnFilterInput(header.id, val)}
                            />
                          );
                        }
                        return (
                          <Input
                            placeholder=""
                            value={columnFilters[header.id] ?? ''}
                            onChange={e => handleColumnFilterInput(header.id, e.target.value)}
                            className="h-7 rounded-sm border-muted-foreground/20 bg-transparent text-xs focus-visible:ring-1"
                          />
                        );
                      })()
                    : null}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={allColumns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">Loading...</div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={allColumns.length} className="h-24 text-center">
                  <div className="text-muted-foreground">No results.</div>
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className="truncate"
                      style={{
                        width: `var(--col-${cell.column.id}-size)`,
                        maxWidth: `var(--col-${cell.column.id}-size)`
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <DataTablePagination table={table} totalCount={totalCount} />
    </div>
  );
}
